// CommonModal.tsx
import type { ReactNode } from 'react';
import React, { useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
} from '@mui/material';
import OutlineButton from '../MUICustomButton/OutlineButton';
import GradientButton from '../MUICustomButton/GradientButton';

interface CommonModalProps {
  open: boolean;
  children?: ReactNode;
  onClose: () => void;
  title: string;
  worksheets: string[];
  onExport: (selectedSheets: string[]) => void;
}

const CommonModal: React.FC<CommonModalProps> = ({
  open,
  children,
  onClose,
  title,
  worksheets,
  onExport,
}) => {
  const [selectedSheets, setSelectedSheets] = React.useState<string[]>([]);

  const handleToggle = (value: string) => () => {
    const currentIndex = selectedSheets.indexOf(value);
    const newChecked = [...selectedSheets];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setSelectedSheets(newChecked);
  };

  useEffect(() => {
    if (!open) {
      setSelectedSheets([]);
    }
  }, [open]);

  const handleExportClick = (): void => {
    onExport(selectedSheets);
    setSelectedSheets([]); // Clear selection after export
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Box
          sx={{
            maxHeight: 300,
            overflow: 'auto',
            mb: 2,
            '&::-webkit-scrollbar': {
              width: '0.4em',
            },
            '&::-webkit-scrollbar-track': {
              boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
              webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,.1)',
              outline: '1px solid slategrey',
            },
          }}
        >
          {worksheets.length > 0 ? (
            <List>
              {worksheets.map((name) => (
                <ListItem key={name} button onClick={handleToggle(name)}>
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={selectedSheets.indexOf(name) !== -1}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText primary={name} />
                </ListItem>
              ))}
            </List>
          ) : (
            children
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <OutlineButton color="gray" onClick={onClose} width="120px">
        Clear
        </OutlineButton>
        <Box sx={{ml: 1 }}>
        <GradientButton color="red" width="130px" onClick={handleExportClick}>
        Download
        </GradientButton>
         </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default CommonModal;
